/**
 * Route Tracking Module
 * Tracks page views and route changes
 * Integrates with Vue Router
 */

import { update_page_context, push_to_data_layer, ANALYTICS_CONFIG } from './index.js';

/**
 * Setup analytics on router instance
 * Call this after creating your router instance
 * @param {Object} router - Vue Router instance
 * @returns {void}
 */
export function setup_route_tracking(router) {
  if (!router) {
    if (ANALYTICS_CONFIG.debug) {
      console.warn('[Analytics] Router instance not provided');
    }
    return;
  }

  // Hook into router navigation
  router.afterEach((to, from) => {
    try {
      // Update page context with new route information
      const updated_page_context = update_page_context(to);

      // Create pageview tracking payload
      const pageview_payload = {
        event: updated_page_context['event'] || 'pageview',
        page: updated_page_context,
        from_route: from.path,
        to_route: to.path,
        timestamp: Date.now()
      };

      // Push to dataLayer
      push_to_data_layer(pageview_payload);

      if (ANALYTICS_CONFIG.debug) {
        console.log('[Analytics] Route change tracked:', {
          from: from.path,
          to: to.path,
          page_context: updated_page_context
        });
      }
    } catch (error) {
      if (ANALYTICS_CONFIG.debug) {
        console.error('[Analytics] Error in router hook:', error);
      }
    }
  });
}

/**
 * Alternative setup using beforeEach guard if you need route change blocking
 * @param {Object} router - Vue Router instance
 * @returns {void}
 */
export function setup_route_tracking_with_guard(router) {
  if (!router) {
    if (ANALYTICS_CONFIG.debug) {
      console.warn('[Analytics] Router instance not provided');
    }
    return;
  }

  router.beforeEach((to, from, next) => {
    try {
      if (ANALYTICS_CONFIG.debug) {
        console.log('[Analytics] Navigating to:', to.path);
      }

      // Always allow navigation
      next();
    } catch (error) {
      if (ANALYTICS_CONFIG.debug) {
        console.error('[Analytics] Error in beforeEach:', error);
      }
      next();
    }
  });

  // Track after navigation completes
  router.afterEach((to, from) => {
    try {
      const updated_page_context = update_page_context(to);

            const pageview_payload = {
                event: updated_page_context['event'] || 'pageview',
        page: updated_page_context,
        from_route: from.path,
        to_route: to.path,
        timestamp: Date.now()
      };

      push_to_data_layer(pageview_payload);

      if (ANALYTICS_CONFIG.debug) {
        console.log('[Analytics] Route change tracked via afterEach:', {
          from: from.path,
          to: to.path
        });
      }
    } catch (error) {
      if (ANALYTICS_CONFIG.debug) {
        console.error('[Analytics] Error in afterEach:', error);
      }
    }
  });
}

/**
 * Track current page view manually
 * Useful when initial page load is not captured by router
 * @param {Object} route - Current route object
 * @returns {void}
 */
export function track_pageview(route) {
  if (!route) {
    if (ANALYTICS_CONFIG.debug) {
      console.warn('[Analytics] Route object not provided for manual pageview tracking');
    }
    return;
  }

  try {
    const page_context = update_page_context(route);

    const pageview_payload = {
      event: 'pageview',
      page: page_context,
      timestamp: Date.now(),
      manual_track: true
    };

    push_to_data_layer(pageview_payload);

    if (ANALYTICS_CONFIG.debug) {
      console.log('[Analytics] Manual pageview tracked:', page_context);
    }
  } catch (error) {
    if (ANALYTICS_CONFIG.debug) {
      console.error('[Analytics] Error in manual pageview tracking:', error);
    }
  }
}

/**
 * Track scroll depth
 * @param {number} depth - Scroll depth percentage (0-100)
 * @returns {boolean} - True if tracked
 */
export function track_scroll_depth(depth = 0) {
  if (typeof window === 'undefined' || !Array.isArray(window.dataLayer)) {
    return false;
  }

  try {
    const payload = {
      event: 'scroll_depth',
      scroll_depth: Math.min(100, Math.max(0, Number(depth))),
      timestamp: Date.now()
    };

    push_to_data_layer(payload);

    if (ANALYTICS_CONFIG.debug) {
      console.log('[Analytics] Scroll depth tracked:', depth + '%');
    }

    return true;
  } catch (error) {
    if (ANALYTICS_CONFIG.debug) {
      console.error('[Analytics] Error tracking scroll depth:', error);
    }
    return false;
  }
}

/**
 * Track time on page
 * @param {number} seconds - Time spent on page in seconds
 * @returns {boolean} - True if tracked
 */
export function track_time_on_page(seconds = 0) {
  if (typeof window === 'undefined' || !Array.isArray(window.dataLayer)) {
    return false;
  }

  try {
    const payload = {
      event: 'time_on_page',
      time_on_page_seconds: Number(seconds) || 0,
      timestamp: Date.now()
    };

    push_to_data_layer(payload);

    if (ANALYTICS_CONFIG.debug) {
      console.log('[Analytics] Time on page tracked:', seconds + 's');
    }

    return true;
  } catch (error) {
    if (ANALYTICS_CONFIG.debug) {
      console.error('[Analytics] Error tracking time on page:', error);
    }
    return false;
  }
}
